import { Avatar, Col, Row } from "antd";
import React from "react";
import {
    ResponsiveContainer,
    PieChart, Pie, Cell, Label, BarChart, Bar,
    ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from "recharts";
import { isValidValue } from "../../../helpers/FormHelper";

const CDN_TEAM_LOGO = (id) => (id ? `https://media.api-sports.io/football/teams/${id}.png` : undefined);

const COLORS = {
    team1: "#2563eb",   
    team2: "#16a34a",   
    draw: "#94a3b8",   
    win: "#22c55e",
    loss: "#ef4444",
    scored: "#2563eb",
    conc: "#f59e0b",   
    grid: "#e5e7eb",
    text: "#0f172a",
    fg2: "#64748b",
};

const OU_THRESHOLDS = [0.5, 1.5, 2.5, 3.5, 4.5];

function buildOUBucketsFromSeries(series = []) {
    const N = series.length || 1;
    return OU_THRESHOLDS.map((t) => {
        const overC = series.reduce((acc, m) => acc + ((m.scored + m.conceded) > t ? 1 : 0), 0);
        const underC = N - overC;
        const over = (overC / N) * 100;
        const under = (underC / N) * 100;
        return { k: String(t), over, under, overC, underC, total: N };
    });
}

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDate = (iso) => {
    const d = new Date(iso);
    if (isNaN(d)) return "";
    return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;
};
const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const resultOf = (f) => {
    const sh = f?.score?.fulltime || f?.score?.halftime || {};
    const home = (sh?.home ?? f?.goals?.home ?? 0) | 0;
    const away = (sh?.away ?? f?.goals?.away ?? 0) | 0;
    return { home, away };
};
const winnerSide = (f) => (f?.teams?.home?.winner ? "home" : f?.teams?.away?.winner ? "away" : "draw");

function buildH2HSeries(h2h = [], t1, t2, t1Name = "Team 1", t2Name = "Team 2") {
    const sorted = [...h2h].sort((a, b) => new Date(a?.fixture?.date || 0) - new Date(b?.fixture?.date || 0));

    let t1Wins = 0, t2Wins = 0, draws = 0;
    let t1Goals = 0, t2Goals = 0;

    const perMatch = sorted.map((f, i) => {
        const { home, away } = resultOf(f);
        const hid = f?.teams?.home?.id, aid = f?.teams?.away?.id;
        const d = fmtDate(f?.fixture?.date);
        const g1 = hid === t1 ? home : aid === t1 ? away : 0;
        const g2 = hid === t2 ? home : aid === t2 ? away : 0;

        t1Goals += g1; t2Goals += g2;

        const w = winnerSide(f);
        if (w === "draw") draws++;
        else if ((w === "home" && hid === t1) || (w === "away" && aid === t1)) t1Wins++;
        else t2Wins++;

        return { i: i + 1, date: d, totalGoals: safeNum(g1 + g2), t1Goals: g1, t2Goals: g2, label: d };
    });

    const avgTotal = perMatch.length ? perMatch.reduce((s, x) => s + x.totalGoals, 0) / perMatch.length : 0;
    const ouBuckets = buildOUBucketsFromTotals(perMatch, "totalGoals");

    return {
        perMatch, avgTotal, ouBuckets,
        pieWinsData: [
            { name: `${t1Name}`, value: t1Wins, color: COLORS.team1 },
            { name: "Draws", value: draws, color: COLORS.draw },
            { name: `${t2Name}`, value: t2Wins, color: COLORS.team2 },
        ],
        pieGoalsData: [
            { name: `${t1Name}`, value: t1Goals, color: COLORS.team1 },
            { name: `${t2Name}`, value: t2Goals, color: COLORS.team2 },
        ],
    };
}

function getTeamPerspective(f, teamId) {
    const { home, away } = resultOf(f);
    const hid = f?.teams?.home?.id, aid = f?.teams?.away?.id;
    const oursHome = hid === teamId;
    const scored = oursHome ? home : aid === teamId ? away : 0;
    const conceded = oursHome ? away : aid === teamId ? home : 0;
    let outcome = "D";
    if (scored > conceded) outcome = "W";
    else if (scored < conceded) outcome = "L";
    return { scored, conceded, outcome };
}

function buildOUBucketsFromTotals(list = [], key = "totalGoals") {
    const N = list.length || 1;
    return OU_THRESHOLDS.map((t) => {
        const overC = list.reduce((acc, m) => acc + ((safeNum(m[key], 0) > t) ? 1 : 0), 0);
        const underC = N - overC;
        return {
            k: String(t),
            over: (overC / N) * 100,
            under: (underC / N) * 100,
            overC, underC, total: N,
        };
    });
}

function buildRecentTeamSeries(fixtures = [], teamId, N = 10) {
    const subset = fixtures
        .slice()
        .sort((a, b) => new Date(a?.fixture?.date || 0) - new Date(b?.fixture?.date || 0))
        .slice(-N);

    let w = 0, d = 0, l = 0, sumScored = 0, sumConceded = 0;

    const series = subset.map((f, i) => {
        const { scored, conceded, outcome } = getTeamPerspective(f, teamId);
        if (outcome === "W") w++; else if (outcome === "D") d++; else l++;
        sumScored += scored; sumConceded += conceded;
        return { i: i + 1, date: fmtDate(f?.fixture?.date), scored, conceded };
    });

    const avgScored = series.length ? sumScored / series.length : 0;
    const avgConceded = series.length ? sumConceded / series.length : 0;
    const ouBuckets = buildOUBucketsFromSeries(series);

    return {
        series, avgScored, avgConceded, ouBuckets,
        pieWDL: [
            { name: "Wins", value: w, color: COLORS.win },
            { name: "Draws", value: d, color: COLORS.draw },
            { name: "Losses", value: l, color: COLORS.loss },
        ],
        pieForAgainst: [
            { name: "Scored", value: sumScored, color: COLORS.scored },
            { name: "Conceded", value: sumConceded, color: COLORS.conc },
        ],
    };
}

const CenterDonutLabel = ({ viewBox, title, total }) => {
    if (!viewBox || !Number.isFinite(viewBox.cx) || !Number.isFinite(viewBox.cy)) return null;
    const { cx, cy } = viewBox;
    return (
        <>
            <text x={cx} y={cy - 6} textAnchor="middle" fill={COLORS.text} fontWeight={800} fontSize={16}>{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill={COLORS.fg2} fontWeight={600} fontSize={12}>{title}</text>
        </>
    );
};

const PrettyTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="an-tip">
            <div className="an-tip-h">Match {label}</div>
            {payload.map((p) => (
                <div key={p.dataKey} className="an-tip-r">
                    <span className="an-dot" style={{ background: p.color }} />
                    <span className="an-k">{p.name}</span>
                    <span className="an-v">{p.value}</span>
                </div>
            ))}
        </div>
    );
};

const OUTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0]?.payload;
    if (!p) return null;
    return (
        <div className="an-tip">
            <div className="an-tip-h">O/U {label}</div>
            <div className="an-tip-r">
                <span className="an-dot" style={{ background: COLORS.win }} />
                <span className="an-k">Over</span>
                <span className="an-v">{p.over.toFixed(0)}% ({p.overC}/{p.total})</span>
            </div>
            <div className="an-tip-r">
                <span className="an-dot" style={{ background: COLORS.loss }} />
                <span className="an-k">Under</span>
                <span className="an-v">{p.under.toFixed(0)}% ({p.underC}/{p.total})</span>
            </div>
        </div>
    );
};

const Section = ({ title, subtitle, children }) => (
    <div className="an-section">
        <h3 className="an-title">{title}</h3>
        {subtitle && <p className="an-sub">{subtitle}</p>}
        <div className="an-grid">{children}</div>
    </div>
);
const ChartCard = ({ title, children }) => (
    <div className="an-card">
        <div className="an-card-h">{title}</div>
        <div className="an-card-b">{children}</div>
    </div>
);
const Segments = ({ value, onChange, options = [5, 10, 25, 50] }) => (
    <div className="an-seg">
        {options.map((n) => (
            <button
                key={n}
                className={value === n ? "an-seg-btn is-active" : "an-seg-btn"}
                onClick={() => onChange(n)}
            >
                Last {n}
            </button>
        ))}
    </div>
);

export default function MatchAnalytics({
    dispatch,
    t1, t2, h2h,
    t1Name, t2Name,
    t1Logo, t2Logo,
    last1 = [],
    last2 = [],
    lastMatches,
}) {
    const crest1 = t1Logo || CDN_TEAM_LOGO(t1);
    const crest2 = t2Logo || CDN_TEAM_LOGO(t2);

    const h2hData = React.useMemo(
        () => buildH2HSeries(h2h, Number(t1), Number(t2), t1Name, t2Name),
        [h2h, t1, t2, t1Name, t2Name]
    );

    const homeData = React.useMemo(() => buildRecentTeamSeries(last1, Number(t1), lastMatches), [last1, t1, lastMatches]);
    const awayData = React.useMemo(() => buildRecentTeamSeries(last2, Number(t2), lastMatches), [last2, t2, lastMatches]);

    const donutStyle = { innerRadius: 64, outerRadius: 104, startAngle: 90, endAngle: -270, cornerRadius: 6, paddingAngle: 2 };

    const onLastMatchesChange = (amount) => {
        const isValidAmount = isValidValue(amount);
        if (isValidAmount === false) {
            dispatch({ type: 'set_last_matches', payload: 5 });
            return;
        }

        dispatch({ type: 'set_last_matches', payload: amount });
    };

    return (
        <section className="px-container">
            <div className="an-wrap">
                <style>{css}</style>

                <Section
                    title="Head-to-Head Analytics"
                    subtitle="Results split, goals trend with average, and total goals share."
                >
                    {/* Donut 1: Results split */}
                    <Row gutter={24}>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title="Results split">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={h2hData.pieWinsData} dataKey="value" nameKey="name" {...donutStyle}>
                                            {h2hData.pieWinsData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(props) => (
                                                <CenterDonutLabel {...props} title="Results" total={h2hData.pieWinsData.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title="Total goals per match">
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart data={h2hData.perMatch}>
                                        <defs>
                                            <linearGradient id="gradGoals" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={COLORS.text} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={COLORS.text} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="i" tick={{ fontSize: 12 }} label={{ value: "Match # (old → new)", position: "insideBottom", offset: -2 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<PrettyTooltip />} />
                                        <Legend />
                                        <Area type="monotone" dataKey="totalGoals" name="Total Goals" stroke="none" fill="url(#gradGoals)" />
                                        <Line type="monotone" dataKey="totalGoals" name="Total Goals" stroke={COLORS.text} strokeWidth={2.25} dot={false} />
                                        <ReferenceLine y={h2hData.avgTotal} stroke={COLORS.draw} strokeDasharray="6 6" label={`Avg ${h2hData.avgTotal.toFixed(2)}`} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Head-to-Head Over/Under`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={h2hData.ouBuckets} barCategoryGap={20} barGap={6}>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="k" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<OUTooltip />} />
                                        <Legend />
                                        <Bar dataKey="over" name="Over" fill={COLORS.win} radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="under" name="Under" fill={COLORS.loss} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title="Goals share">
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={h2hData.pieGoalsData} dataKey="value" nameKey="name" {...donutStyle}>
                                            {h2hData.pieGoalsData.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(props) => (
                                                <CenterDonutLabel {...props} title="Total Goals" total={h2hData.pieGoalsData.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                    </Row>
                </Section>

                {/* ================== HOME ================== */}
                <Section
                    title={<span className="an-teamtitle"><Avatar size={40} style={{ padding: 5 }} src={crest1} alt="" /> Team Analytics</span>}
                    subtitle="Choose the sample size; all charts update together."
                >
                    <Segments value={lastMatches} onChange={onLastMatchesChange} />
                    <Row gutter={24}>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`W/D/L (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={homeData.pieWDL} dataKey="value" nameKey="name" {...donutStyle}>
                                            {homeData.pieWDL.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(p) => (
                                                <CenterDonutLabel {...p} title="Matches" total={homeData.pieWDL.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Goals per match (scored & conceded)`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart data={homeData.series}>
                                        <defs>
                                            <linearGradient id="gradScored1" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={COLORS.scored} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={COLORS.scored} stopOpacity={0.02} />
                                            </linearGradient>
                                            <linearGradient id="gradConceded1" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={COLORS.conc} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={COLORS.conc} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="i" tick={{ fontSize: 12 }} label={{ value: "Match # (old → new)", position: "insideBottom", offset: -2 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<PrettyTooltip />} />
                                        <Legend />
                                        <Area type="monotone" dataKey="scored" name="Scored" fill="url(#gradScored1)" stroke="none" />
                                        <Area type="monotone" dataKey="conceded" name="Conceded" fill="url(#gradConceded1)" stroke="none" />
                                        <Line type="monotone" dataKey="scored" name="Scored" stroke={COLORS.scored} strokeWidth={2.1} dot={false} />
                                        <Line type="monotone" dataKey="conceded" name="Conceded" stroke={COLORS.conc} strokeWidth={2.1} dot={false} />
                                        <ReferenceLine y={homeData.avgScored} stroke={COLORS.scored} strokeDasharray="6 6" label={`Avg Scored ${homeData.avgScored.toFixed(2)}`} />
                                        <ReferenceLine y={homeData.avgConceded} stroke={COLORS.conc} strokeDasharray="6 6" label={`Avg Conceded ${homeData.avgConceded.toFixed(2)}`} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Over/Under (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={homeData.ouBuckets} barCategoryGap={20} barGap={6}>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="k" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<OUTooltip />} />
                                        <Legend />
                                        <Bar dataKey="over" name="Over" fill={COLORS.win} radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="under" name="Under" fill={COLORS.loss} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Goals share (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={homeData.pieForAgainst} dataKey="value" nameKey="name" {...donutStyle}>
                                            {homeData.pieForAgainst.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(p) => (
                                                <CenterDonutLabel {...p} title="Total Goals" total={homeData.pieForAgainst.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                    </Row>
                </Section>

                {/* ================== AWAY ================== */}
                <Section
                    title={<span className="an-teamtitle"><Avatar size={40} style={{ padding: 5 }} src={crest2} alt="" /> Team Analytics</span>}
                    subtitle="Choose the sample size; all charts update together."
                >
                    <Segments value={lastMatches} onChange={onLastMatchesChange} />
                    <Row gutter={24}>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`W/D/L (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={awayData.pieWDL} dataKey="value" nameKey="name" {...donutStyle}>
                                            {awayData.pieWDL.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(p) => (
                                                <CenterDonutLabel {...p} title="Matches" total={awayData.pieWDL.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Goals per match (scored & conceded)`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <ComposedChart data={awayData.series}>
                                        <defs>
                                            <linearGradient id="gradScored2" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={COLORS.scored} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={COLORS.scored} stopOpacity={0.02} />
                                            </linearGradient>
                                            <linearGradient id="gradConceded2" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={COLORS.conc} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={COLORS.conc} stopOpacity={0.02} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="i" tick={{ fontSize: 12 }} label={{ value: "Match # (old → new)", position: "insideBottom", offset: -2 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<PrettyTooltip />} />
                                        <Legend />
                                        <Area type="monotone" dataKey="scored" name="Scored" fill="url(#gradScored2)" stroke="none" />
                                        <Area type="monotone" dataKey="conceded" name="Conceded" fill="url(#gradConceded2)" stroke="none" />
                                        <Line type="monotone" dataKey="scored" name="Scored" stroke={COLORS.scored} strokeWidth={2.1} dot={false} />
                                        <Line type="monotone" dataKey="conceded" name="Conceded" stroke={COLORS.conc} strokeWidth={2.1} dot={false} />
                                        <ReferenceLine y={awayData.avgScored} stroke={COLORS.scored} strokeDasharray="6 6" label={`Avg Scored ${awayData.avgScored.toFixed(2)}`} />
                                        <ReferenceLine y={awayData.avgConceded} stroke={COLORS.conc} strokeDasharray="6 6" label={`Avg Conceded ${awayData.avgConceded.toFixed(2)}`} />
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                    </Row>
                    <Row gutter={24}>
                        <Col xl={18} lg={18} md={12} sm={24} xs={24} style={{ marginBottom: 20 }}>
                            <ChartCard title={`Over/Under (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={awayData.ouBuckets} barCategoryGap={20} barGap={6}>
                                        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" />
                                        <XAxis dataKey="k" tick={{ fontSize: 12 }} />
                                        <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 12 }} />
                                        <Tooltip content={<OUTooltip />} />
                                        <Legend />
                                        <Bar dataKey="over" name="Over" fill={COLORS.win} radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="under" name="Under" fill={COLORS.loss} radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                        <Col xl={6} lg={6} md={12} sm={24} xs={24}>
                            <ChartCard title={`Goals share (last ${lastMatches})`}>
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie data={awayData.pieForAgainst} dataKey="value" nameKey="name" {...donutStyle}>
                                            {awayData.pieForAgainst.map((e, i) => <Cell key={i} fill={e.color} />)}
                                            <Label content={(p) => (
                                                <CenterDonutLabel {...p} title="Total Goals" total={awayData.pieForAgainst.reduce((s, x) => s + x.value, 0)} />
                                            )} />
                                        </Pie>
                                        <Legend />
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </Col>
                    </Row>
                </Section>
            </div>
        </section>
    );
}

const css = `
.px-container { background:#f7f7f9; padding:30px; }
@media (max-width: 520px) { .px-container { padding:16px; } }

.an-wrap { max-width: 1380px; margin: 0 auto; }
.an-section { margin: 0 0 26px; }
.an-title { margin:0; font-size:18px; font-weight:800; color:${COLORS.text}; display:flex; align-items:center; gap:8px; }
.an-sub { margin:4px 0 14px; color:${COLORS.fg2}; }

/* 3-up layout -> 2-up -> 1-up */
.an-grid { gap:14px; }

.an-card {
  background:#fff;
  border:1px solid rgba(2,6,23,.08);
  border-radius:14px;
  overflow:hidden;
  display:flex; flex-direction:column;
  transition: box-shadow .25s ease, transform .12s ease;
}
.an-card:hover { box-shadow: 0 8px 28px rgba(2,6,23,.08); transform: translateY(-1px); }
.an-card-h { font-weight:800; color:${COLORS.text}; padding:12px 14px; border-bottom:1px solid rgba(2,6,23,.06); }
.an-card-b { padding:8px 4px 4px; min-height: 230px; }

/* segmented control */
.an-seg { display:flex; flex-wrap:wrap; gap:8px; margin:8px 0 12px; }
.an-seg-btn {
  border:1px solid rgba(2,6,23,.12);
  background:#fff;
  padding:6px 12px;
  border-radius:999px;
  font-weight:700;
  color:${COLORS.text};
  cursor:pointer;
  transition: all .15s ease;
}
.an-seg-btn:hover { border-color:#0ea5e9; color:#0ea5e9; }
.an-seg-btn.is-active { background:#0ea5e9; color:#fff; border-color:#0ea5e9; }

/* tooltip */
.an-tip { background:#0f172a; color:#fff; border-radius:10px; padding:10px 12px; box-shadow: 0 8px 24px rgba(2,6,23,.25); }
.an-tip-h { font-weight:800; margin-bottom:6px; opacity:.95; }
.an-tip-r { display:flex; align-items:center; gap:8px; font-size:13px; }
.an-dot { width:10px; height:10px; border-radius:50%; display:inline-block; }
.an-k { opacity:.9; min-width:88px; }
.an-v { font-weight:800; }
`;
